import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Alert,
  Spinner,
  Badge,
  Nav,
  NavItem,
  NavLink
} from "reactstrap";
import moment from "moment";
import { toast } from "react-toastify";
import { API } from "../../../api/API";
import { getData, postData } from "../../../CommonElements/Functions/PostGetFun";
import Loader from "../../../Layout/Loader";

export interface AttachmentItem {
  type: "image" | "pdf";
  mediaId: number;
  uri?: string;
  fileName: string;
  downUrl?: string;
}

export interface ResolvedMedia {
  id: string;
  type: "image" | "pdf";
  url: string;
  fileName: string;
}

export interface ApplyLeaveModalProps {
  isOpen: boolean;
  toggle: () => void;
  empId?: string | number;
  leaveRequestId?: string | number;
  status?: "Apply" | "View" | string;
  teamView?: boolean;
  navigateFrom?: string;
  leaveStatuses?: Record<string | number, string>;
  onSuccess?: () => void;
  headerDate?: string;
  configuration?: Record<string, any>;
  autoOpenMedia?: boolean;
}

const DEFAULT_STATUS_MAP: Record<string | number, string> = {
  0: "Submit",
  1: "Raised",
  2: "Approved",
  3: "Rejected",
  4: "Cancelled",
};

const ApplyLeaveModal: React.FC<ApplyLeaveModalProps> = ({
  isOpen,
  toggle,
  empId,
  leaveRequestId,
  status = "Apply",
  teamView = false,
  navigateFrom,
  leaveStatuses = DEFAULT_STATUS_MAP,
  onSuccess,
  headerDate,
  configuration,
  autoOpenMedia = false,
}) => {
  const serverUrl = localStorage.getItem("serverUrl") || "";
  const MAX_ATTACHMENTS = 10;

  // System Configuration for Backdate leave
  const isBackdateLeaveAllowed =
    String(configuration?.["leave_backdate"] || "").toLowerCase().trim() === "on" ||
    String(configuration?.["leave_backdate"] || "").toLowerCase().trim() === "yes" ||
    String(configuration?.["leave_backdate"] || "").toLowerCase().trim() === "true";

  const backdateDaysStr = configuration?.["leave_backdate_day"];
  const backdateDays = backdateDaysStr ? parseInt(backdateDaysStr, 10) : null;

  let minLeaveDateStr: string | undefined;
  if (isBackdateLeaveAllowed) {
    if (backdateDays !== null && !isNaN(backdateDays)) {
      minLeaveDateStr = moment().subtract(backdateDays, "days").format("YYYY-MM-DD");
    } else {
      minLeaveDateStr = moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD");
    }
  }

  // Form & UI States
  const [leaveTypes, setLeaveTypes] = useState<{ label: string; value: string }[]>([]);
  const [leaveTypeObj, setLeaveTypeObj] = useState<Record<string, string>>({});
  const [fromDate, setFromDate] = useState<string>(moment().format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState<string>(moment().format("YYYY-MM-DD"));
  const [selectedLeaveType, setSelectedLeaveType] = useState<string>("");
  const [remark, setRemark] = useState<string>("");

  const [leaveData, setLeaveData] = useState<any>({});
  const [leaveDaysCount, setLeaveDaysCount] = useState<number>(1);
  const [leaveBalances, setLeaveBalances] = useState<any[]>([]);
  const [balc, setBalc] = useState<string | number>("");
  const [showNote, setShowNote] = useState<boolean>(false);

  const [loader, setLoader] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  // Attachments state (when applying)
  const [attachments, setAttachments] = useState<AttachmentItem[]>([]);
  const [uploading, setUploading] = useState<boolean>(false);

  // Resolved Media Modal & Image Lightbox states (when viewing)
  const [resolvedMedia, setResolvedMedia] = useState<{ images: ResolvedMedia[]; pdfs: ResolvedMedia[] }>({
    images: [],
    pdfs: [],
  });
  const [mediaLoading, setMediaLoading] = useState<boolean>(false);
  const [mediaModalOpen, setMediaModalOpen] = useState<boolean>(false);
  const [selectedMediaTab, setSelectedMediaTab] = useState<"images" | "pdfs">("images");

  const [imageViewerOpen, setImageViewerOpen] = useState<boolean>(false);
  const [imageViewerIndex, setImageViewerIndex] = useState<number>(0);
  const [imageViewerList, setImageViewerList] = useState<{ url: string; fileName: string }[]>([]);

  const mediaMetaCache = useRef(new Map<string, ResolvedMedia>());

  // Sub-modals state
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
  const [openRemarkModal, setOpenRemarkModal] = useState<boolean>(false);
  const [rejectionRemark, setRejectionRemark] = useState<string>("");
  const [leaveAction, setLeaveAction] = useState<"approve" | "reject" | "cancel">("approve");

  const attachmentsCount = attachments.length;

  // Initialize dates based on props
  useEffect(() => {
    if (navigateFrom === "AdhocLeave" && headerDate) {
      setSelectedLeaveType("LWP");
      setBalc("LWP");
      if (moment(headerDate).isBefore(moment().startOf("year"))) {
        setFromDate(moment().format("YYYY-MM-DD"));
        setToDate(moment().format("YYYY-MM-DD"));
      } else {
        setFromDate(moment(headerDate).format("YYYY-MM-DD"));
        setToDate(moment(headerDate).format("YYYY-MM-DD"));
      }
    }
  }, [navigateFrom, headerDate]);

  // Load Leave Types & Balances or Existing Leave Details when modal opens
  useEffect(() => {
    if (!isOpen) return;

    getTypesOfLeave();
    if (status !== "Apply" && leaveRequestId) {
      getLeaveById();
    } else {
      getLeaveBalances();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, status, leaveRequestId]);

  // Calculate Leave Days when dates or leave type change
  useEffect(() => {
    if (!isOpen || status !== "Apply") return;

    const calculateDays = async () => {
      if (!fromDate || !toDate) {
        setLeaveDaysCount(0);
        return;
      }
      if (moment(toDate).isBefore(moment(fromDate), "day")) {
        setLeaveDaysCount(0);
        return;
      }

      if (!selectedLeaveType) {
        const diff = moment(toDate).diff(moment(fromDate), "days") + 1;
        setLeaveDaysCount(diff > 0 ? diff : 0);
        return;
      }

      try {
        const url = `${serverUrl}${API.checkDays}`;
        const params = {
          from_date: moment(fromDate).format("YYYY-MM-DD"),
          to_date: moment(toDate).format("YYYY-MM-DD"),
          leave_type: selectedLeaveType,
        };
        const { status: code, data } = await postData(url, true, params);
        if (code === 200 && data?.data?.count !== undefined) {
          setLeaveDaysCount(Number(data.data.count) || 0);
        } else if (code === 200 && data?.count !== undefined) {
          setLeaveDaysCount(Number(data.count) || 0);
        } else {
          // Fallback: simple difference in days
          const diff = moment(toDate).diff(moment(fromDate), "days") + 1;
          setLeaveDaysCount(diff > 0 ? diff : 0);
        }
      } catch (err) {
        const diff = moment(toDate).diff(moment(fromDate), "days") + 1;
        setLeaveDaysCount(diff > 0 ? diff : 0);
      }
    };

    calculateDays();
  }, [fromDate, toDate, selectedLeaveType, isOpen, status, serverUrl]);

  // Fetch Leave Types
  const getTypesOfLeave = async () => {
    setLoader(true);
    try {
      const url = `${serverUrl}${API.getLeaveTypes}`;
      const { status: code, data } = await getData(url, true);
      const dt = data?.data ?? data;
      if (code === 200 && dt) {
        if (Array.isArray(dt)) {
          const list = dt.map((item: any) => ({
            label: item.name || item.LeaveType || item.label || item,
            value: item.id || item.code || item.value || item,
          }));
          setLeaveTypes(list);
        } else if (typeof dt === "object") {
          const list = Object.keys(dt).map((key) => ({
            label: dt[key],
            value: key,
          }));
          setLeaveTypes(list);
          setLeaveTypeObj(dt);
        }
      }
    } catch (err) {
      console.error("Error fetching leave types", err);
    } finally {
      setLoader(false);
    }
  };

  // Fetch Leave By ID (for view/manager mode)
  const getLeaveById = async () => {
    if (!leaveRequestId) return;
    setLoader(true);
    try {
      const url = `${serverUrl}${API.getLeaveById}`;
      const { status: code, data } = await getData(url, true, { leave_id: leaveRequestId });
      const payload = data?.data ?? data;
      if (code === 200 && payload) {
        const obj = Array.isArray(payload) ? payload[0] : payload;
        setLeaveData(obj || {});
        if (obj?.Attachment) {
          resolveMediaByIds(String(obj.Attachment)).then((media) => {
            setResolvedMedia(media);
            setSelectedMediaTab(media.images.length > 0 ? "images" : "pdfs");
            if (autoOpenMedia) {
              setMediaModalOpen(true);
            }
          });
        }
      }
    } catch (err) {
      console.error("Error fetching leave details", err);
    } finally {
      setLoader(false);
    }
  };

  // Fetch Employee Leave Balances
  const getLeaveBalances = async () => {
    try {
      const url = `${serverUrl}${API.getEmployeeLeaveBalance}`;
      const params = empId ? { employee_id: empId } : {};
      const { status: code, data } = await getData(url, true, params);
      const payload = data?.data ?? data;
      if (code === 200 && payload) {
        if (Array.isArray(payload)) {
          setLeaveBalances(payload);
        } else if (typeof payload === "object") {
          const arr = Object.keys(payload).map((k) => payload[k]);
          setLeaveBalances(arr);
        }
      }
    } catch (err) {
      console.error("Error fetching leave balances", err);
    }
  };

  // Resolve media for a set of IDs (Images vs PDFs) by confirming every mediaid with API.downloadFile
  const resolveMediaByIds = async (rawIdsStr?: string, forceRefresh: boolean = true) => {
    if (!rawIdsStr) return { images: [], pdfs: [] };
    const ids = String(rawIdsStr).split(",").map((s) => s.trim()).filter(Boolean);
    if (!ids.length) return { images: [], pdfs: [] };

    setMediaLoading(true);
    const images: ResolvedMedia[] = [];
    const pdfs: ResolvedMedia[] = [];

    let serverBaseUrl = serverUrl || localStorage.getItem("serverUrl") || "";
    if (serverBaseUrl && !serverBaseUrl.endsWith("/")) {
      serverBaseUrl += "/";
    }

    for (const rawId of ids) {
      if (!forceRefresh && mediaMetaCache.current.has(rawId)) {
        const meta = mediaMetaCache.current.get(rawId)!;
        if (meta.type === "pdf") pdfs.push(meta);
        else images.push(meta);
        continue;
      }

      const mediaUrl = `${serverBaseUrl}media?id=${rawId}`;
      let isPdf = false;
      let fileName = `Attachment_${rawId}`;

      // Query download file API (api/media/download?mediaid=rawId) via CORS-enabled getData helper
      try {
        const downloadApiUrl = `${serverBaseUrl}${API.downloadFile}`;
        const { status: code, data } = await getData(downloadApiUrl, true, { mediaid: rawId });
        const dataObj = data?.data || data || {};
        const rawText = String(data?.raw || (typeof data === "string" ? data : "") || "");
        const dataStr = (JSON.stringify(dataObj) + " " + rawText).toLowerCase();

        const mediatype = String(dataObj.mediatype || dataObj.file_type || dataObj.mime_type || dataObj.type || dataObj.filetype || "").toLowerCase();
        const pdfurl = String(dataObj.pdfurl || dataObj.DownLoadUrl || dataObj.download_url || dataObj.url || dataObj.file_url || dataObj.path || "").toLowerCase();
        const apiFileName = String(dataObj.file_name || dataObj.fileName || dataObj.original_name || dataObj.name || dataObj.title || "");

        if (apiFileName) {
          fileName = apiFileName;
        }

        if (
          rawText.startsWith("%PDF") ||
          rawText.includes("%PDF-") ||
          mediatype.includes("pdf") ||
          pdfurl.includes(".pdf") ||
          apiFileName.toLowerCase().endsWith(".pdf") ||
          /\.pdf(\?|$)/i.test(dataStr) ||
          dataStr.includes('"pdf"') ||
          /\.pdf(\?|$)/i.test(String(rawId))
        ) {
          isPdf = true;
        }
      } catch (err) {
        console.warn("downloadFile API metadata check error for ID:", rawId, err);
        if (/\.pdf(\?|$)/i.test(String(rawId))) {
          isPdf = true;
        }
      }

      const ext = isPdf ? ".pdf" : ".jpg";
      const hasExt = /\.(pdf|jpg|jpeg|png|gif|webp)(\?|$)/i.test(fileName);
      const finalFileName = hasExt ? fileName : `${fileName}${ext}`;

      const meta: ResolvedMedia = {
        id: rawId,
        type: isPdf ? "pdf" : "image",
        fileName: finalFileName,
        url: mediaUrl,
      };

      mediaMetaCache.current.set(rawId, meta);
      if (isPdf) pdfs.push(meta);
      else images.push(meta);
    }

    setMediaLoading(false);
    return { images, pdfs };
  };

  const handleOpenViewMediaModal = async () => {
    if (!leaveData.Attachment) return;
    const media = await resolveMediaByIds(String(leaveData.Attachment), true);
    setResolvedMedia(media);
    setSelectedMediaTab(media.images.length > 0 ? "images" : "pdfs");
    setMediaModalOpen(true);
  };

  // Open Image Lightbox / Full view modal
  const openImageViewer = (list: { url: string; fileName: string }[], initialIndex: number = 0) => {
    setImageViewerList(list);
    setImageViewerIndex(initialIndex);
    setImageViewerOpen(true);
  };

  // Filter Leave Balance when leave type is selected
  const handleSelectLeaveType = (typeValue: string) => {
    setSelectedLeaveType(typeValue);
    if (typeValue === "LWP") {
      setBalc("LWP");
      setShowNote(false);
      return;
    }
    if (leaveBalances.length > 0) {
      const found = leaveBalances.find(
        (item) => String(item.LeaveType || item.type || "").toLowerCase() === String(typeValue).toLowerCase()
      );
      setBalc(found ? found.Balance ?? found.balance ?? 0 : 0);
      setShowNote(true);
    } else {
      setBalc(0);
      setShowNote(true);
    }
  };

  // Upload Attachment (Image base64 or PDF base64)
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    if (attachmentsCount + files.length > MAX_ATTACHMENTS) {
      toast.warning(`Maximum ${MAX_ATTACHMENTS} attachments allowed.`);
      return;
    }

    setUploading(true);
    try {
      let serverBaseUrl = serverUrl || localStorage.getItem("serverUrl") || "";
      if (serverBaseUrl && !serverBaseUrl.endsWith("/")) {
        serverBaseUrl += "/";
      }

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");

        // Convert file to Base64 and upload
        const reader = new FileReader();
        reader.readAsDataURL(file);
        await new Promise<void>((resolve) => {
          reader.onload = async () => {
            const base64Str = (reader.result as string).split(",")[1];
            if (!base64Str) {
              toast.error(`Failed to read file ${file.name}`);
              resolve();
              return;
            }
            const url = `${serverBaseUrl}${API.uploadMediaBase64}`;
            const body = { media: [base64Str] };
            const { status: code, data } = await postData(url, true, body);
            const ids = Array.isArray(data?.data)
              ? data.data.map(Number).filter(Boolean)
              : [Number(data?.data)].filter(Boolean);

            if (code === 200 && ids.length > 0) {
              const newMediaId = ids[0];
              const mediaIdStr = String(newMediaId);

              // Cache metadata immediately so resolveMediaByIds has exact file metadata!
              const meta: ResolvedMedia = {
                id: mediaIdStr,
                type: isPdf ? "pdf" : "image",
                fileName: file.name,
                url: `${serverBaseUrl}media?id=${newMediaId}`,
              };
              mediaMetaCache.current.set(mediaIdStr, meta);

              const newItem: AttachmentItem = {
                type: isPdf ? ("pdf" as const) : ("image" as const),
                mediaId: newMediaId,
                fileName: file.name,
                downUrl: `${serverBaseUrl}media?id=${newMediaId}`,
                uri: !isPdf ? URL.createObjectURL(file) : undefined,
              };

              setAttachments((prev: AttachmentItem[]): AttachmentItem[] => [...prev, newItem].slice(0, MAX_ATTACHMENTS));
              toast.success(`${file.name} uploaded successfully!`);
            } else {
              toast.error(data?.message || `Failed to upload ${file.name}`);
            }
            resolve();
          };
        });
      }
    } catch (err: any) {
      toast.error(err?.message || "Error uploading file");
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  // Submit Leave Request
  const handlePreSubmit = () => {
    setErrorMessage("");

    if (leaveDaysCount <= 0) {
      setErrorMessage("Please select a valid date range");
      return;
    }
    if (!selectedLeaveType) {
      setErrorMessage("Please select leave type.");
      return;
    }
    if (balc !== "" && balc !== "LWP" && Number(balc) < leaveDaysCount) {
      setErrorMessage(`You have insufficient leave balance for ${selectedLeaveType}`);
      return;
    }
    if (!remark.trim()) {
      setErrorMessage("Please mention reason for leave.");
      return;
    }

    setShowConfirmationModal(true);
  };

  const createLeave = async () => {
    setSubmitting(true);
    try {
      const resolvedEmpId = empId || localStorage.getItem("empId") || localStorage.getItem("employee_id") || localStorage.getItem("employeeId") || "";
      const attachmentIds = attachments.map((item) => item.mediaId).filter(Boolean);

      const params = {
        employee_id: resolvedEmpId,
        leave_type: selectedLeaveType,
        from_date: moment(fromDate).format("YYYY-MM-DD"),
        to_date: moment(toDate).format("YYYY-MM-DD"),
        remark: remark.trim(),
        attachment: attachmentIds.length > 0 ? attachmentIds.join(",") : "",
      };

      let serverBaseUrl = serverUrl || localStorage.getItem("serverUrl") || "";
      if (serverBaseUrl && !serverBaseUrl.endsWith("/")) {
        serverBaseUrl += "/";
      }

      const url = `${serverBaseUrl}${API.createLeave}`;
      const { status: code, data } = await postData(url, true, params);

      if (code === 200 || data?.statusCode === 200) {
        toast.success(data?.message || "Leave request submitted successfully!");
        setShowConfirmationModal(false);
        resetForm();
        toggle();
        if (onSuccess) onSuccess();
      } else {
        const errorText = data?.message || data?.error || data?.raw || "Unable to create leave request.";
        setErrorMessage(errorText);
        toast.error(errorText);
        setShowConfirmationModal(false);
      }
    } catch (err: any) {
      toast.error(err?.message || "Error submitting leave request");
      setShowConfirmationModal(false);
    } finally {
      setSubmitting(false);
    }
  };

  // Approval / Rejection / Cancellation by Manager
  const handleApproveReject = async (action: "approve" | "reject" | "cancel") => {
    if (action === "approve") {
      setSubmitting(true);
      try {
        const url = `${serverUrl}${API.leaveApprove}`;
        const { status: code, data } = await postData(url, true, {
          leave__request_id: leaveRequestId,
        });
        if (code === 200 || data?.statusCode === 200) {
          toast.success(data?.message || "Leave approved successfully");
          toggle();
          if (onSuccess) onSuccess();
        } else {
          toast.error(data?.message || "Failed to approve leave");
        }
      } catch (err) {
        toast.error("Failed to approve leave");
      } finally {
        setSubmitting(false);
      }
    } else {
      setLeaveAction(action);
      setRejectionRemark("");
      setOpenRemarkModal(true);
    }
  };

  const submitRejectOrCancel = async () => {
    if (!rejectionRemark.trim()) {
      toast.error("Please mention a reason");
      return;
    }
    setSubmitting(true);
    try {
      let url = "";
      let params: Record<string, any> = {};

      if (leaveAction === "cancel") {
        url = `${serverUrl}${API.leaveCancelled}`;
        params = {
          leave_request_id: leaveRequestId,
          leave_cancel_remark: rejectionRemark.trim(),
        };
      } else {
        url = `${serverUrl}${API.leaveReject}`;
        params = {
          leave__request_id: leaveRequestId,
          leave_reject_remark: rejectionRemark.trim(),
        };
      }

      const { status: code, data } = await postData(url, true, params);
      if (code === 200 || data?.statusCode === 200) {
        toast.success(data?.message || `Leave ${leaveAction === "cancel" ? "cancelled" : "rejected"}`);
        setOpenRemarkModal(false);
        toggle();
        if (onSuccess) onSuccess();
      } else {
        toast.error(data?.message || "Operation failed");
      }
    } catch (err) {
      toast.error("Operation failed");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setAttachments([]);
    setSelectedLeaveType("");
    setRemark("");
    setFromDate(moment().format("YYYY-MM-DD"));
    setToDate(moment().format("YYYY-MM-DD"));
    setErrorMessage("");
    setBalc("");
    setShowNote(false);
  };

  const currentStatusLabel = leaveStatuses[leaveData?.LeaveStatus] || leaveData?.LeaveStatus || "Raised";
  const attachedMediaIds = leaveData.Attachment
    ? String(leaveData.Attachment).split(",").map((s) => s.trim()).filter(Boolean)
    : [];

  return (
    <>
      <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
        <ModalHeader toggle={toggle} className="border-bottom">
          <span className="fw-bold fs-5">
            {status === "Apply" ? "Apply Leave" : `Leave Details - Request #${leaveRequestId || ""}`}
          </span>
        </ModalHeader>

        <ModalBody className="p-4">
          {loader ? (
            <Loader height="200px" />
          ) : (
            <div>
              {errorMessage && (
                <Alert color="danger" className="mb-3 py-2">
                  <strong>Message: </strong> {errorMessage}
                </Alert>
              )}

              {/* Date Selection */}
              <Row className="mb-3">
                <Col md={6}>
                  <FormGroup>
                    <Label className="fw-bold">From date <span className="text-danger">*</span></Label>
                    {status === "Apply" ? (
                      <Input
                        type="date"
                        value={fromDate}
                        min={minLeaveDateStr}
                        onChange={(e) => setFromDate(e.target.value)}
                      />
                    ) : (
                      <Input
                        type="text"
                        disabled
                        value={leaveData.LeaveFrom ? moment(leaveData.LeaveFrom).format("ddd, Do MMM YYYY") : "-"}
                      />
                    )}
                  </FormGroup>
                </Col>

                <Col md={6}>
                  <FormGroup>
                    <Label className="fw-bold">To date <span className="text-danger">*</span></Label>
                    {status === "Apply" ? (
                      <Input
                        type="date"
                        value={toDate}
                        min={fromDate || minLeaveDateStr}
                        max={`${moment().endOf("year").format("YYYY-MM-DD")}`}
                        onChange={(e) => setToDate(e.target.value)}
                      />
                    ) : (
                      <Input
                        type="text"
                        disabled
                        value={leaveData.LeaveTo ? moment(leaveData.LeaveTo).format("ddd, Do MMM YYYY") : "-"}
                      />
                    )}
                  </FormGroup>
                </Col>
              </Row>

              {/* Leave Duration Badge */}
              {status === "Apply" && (
                <div className="mb-3">
                  {leaveDaysCount > 0 ? (
                    <Badge color="light-success" className="text-success p-2 fs-6 border border-success">
                      Selected Duration: <strong>{leaveDaysCount}</strong> {leaveDaysCount === 1 ? "day" : "days"}
                    </Badge>
                  ) : (
                    <Badge color="light-danger" className="text-danger p-2 fs-6 border border-danger">
                      Please select a valid date range
                    </Badge>
                  )}
                </div>
              )}

              {/* Leave Type */}
              <Row className="mb-3">
                <Col md={12}>
                  <FormGroup>
                    <Label className="fw-bold">Leave Type <span className="text-danger">*</span></Label>
                    {status === "Apply" ? (
                      <Input
                        type="select"
                        value={selectedLeaveType}
                        onChange={(e) => handleSelectLeaveType(e.target.value)}
                      >
                        <option value="">-- Select Leave Type --</option>
                        {leaveTypes.map((item) => (
                          <option key={item.value} value={item.value}>
                            {item.label}
                          </option>
                        ))}
                      </Input>
                    ) : (
                      <Input
                        type="text"
                        disabled
                        value={leaveTypeObj[leaveData.LeaveType] || leaveData.LeaveType || "-"}
                      />
                    )}
                  </FormGroup>

                  {showNote && status === "Apply" && (
                    <div className="small fw-semibold mt-1" style={{ color: balc === 0 ? "red" : "#2e8b57" }}>
                      Leave balance for {selectedLeaveType} : {balc === "LWP" ? "0 (Leave Without Pay)" : balc}
                    </div>
                  )}
                </Col>
              </Row>

              {/* Remarks */}
              <Row className="mb-3">
                <Col md={12}>
                  <FormGroup>
                    <Label className="fw-bold">
                      Enter Remark {status === "Apply" && <span className="text-danger">*</span>}
                    </Label>
                    <Input
                      type="textarea"
                      rows={3}
                      disabled={status !== "Apply"}
                      value={status === "Apply" ? remark : leaveData.LeaveReason || ""}
                      onChange={(e) => setRemark(e.target.value)}
                      placeholder="Reason for leave..."
                    />
                  </FormGroup>
                </Col>
              </Row>

              {/* Attachments Section */}
              {status === "Apply" ? (
                <div className="mb-3 border p-3 rounded bg-light">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <Label className="fw-bold m-0">
                      Attachments ({attachmentsCount}/{MAX_ATTACHMENTS})
                    </Label>
                    {uploading && <Spinner size="sm" color="primary" />}
                  </div>

                  <div className="d-flex align-items-center gap-2 mb-3">
                    <Label
                      htmlFor="apply-leave-file-input"
                      className={`btn btn-sm btn-outline-primary mb-0 ${attachmentsCount >= MAX_ATTACHMENTS || uploading ? "disabled" : ""
                        }`}
                    >
                      <i className="fa fa-upload me-1"></i> Add File (Image / PDF)
                    </Label>
                    <input
                      id="apply-leave-file-input"
                      type="file"
                      multiple
                      accept="image/*,application/pdf"
                      style={{ display: "none" }}
                      onChange={handleFileUpload}
                      disabled={attachmentsCount >= MAX_ATTACHMENTS || uploading}
                    />
                    <span className="text-muted small">Max 10 files allowed</span>
                  </div>

                  {attachments.length > 0 && (
                    <div className="d-flex flex-wrap gap-2">
                      {attachments.map((item, idx) => (
                        <div
                          key={`${item.mediaId}-${idx}`}
                          className="position-relative border rounded p-2 bg-white d-flex align-items-center gap-2"
                          style={{ minWidth: "160px", maxWidth: "220px" }}
                        >
                          {item.type === "image" ? (
                            <img
                              src={item.uri || `${serverUrl}media?id=${item.mediaId}`}
                              alt={item.fileName}
                              style={{ width: 40, height: 40, objectFit: "cover", cursor: "pointer" }}
                              className="rounded border"
                              onClick={() =>
                                openImageViewer(
                                  attachments
                                    .filter((a) => a.type === "image")
                                    .map((a) => ({
                                      url: a.uri || `${serverUrl}media?id=${a.mediaId}`,
                                      fileName: a.fileName,
                                    })),
                                  0
                                )
                              }
                            />
                          ) : (
                            <div className="bg-danger text-white rounded p-2 text-center" style={{ width: 40, height: 40 }}>
                              <i className="fa fa-file-pdf-o"></i>
                            </div>
                          )}
                          <div className="text-truncate flex-grow-1 small">
                            <a
                              href={item.downUrl || `${serverUrl}media?id=${item.mediaId}`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-dark fw-semibold text-decoration-none text-truncate d-block"
                            >
                              {item.fileName}
                            </a>
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm btn-circle btn-danger text-white position-absolute top-0 end-0 p-0"
                            style={{ width: "20px", height: "20px", borderRadius: "50%", transform: "translate(30%, -30%)" }}
                            onClick={() => removeAttachment(idx)}
                          >
                            &times;
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                attachedMediaIds.length > 0 && (
                  <div className="mb-3">
                    <Label className="fw-bold d-block">Attachments ({attachedMediaIds.length})</Label>
                    <Button
                      color="primary"
                      onClick={handleOpenViewMediaModal}
                      disabled={mediaLoading}
                    >
                      {mediaLoading ? (
                        <>
                          <Spinner size="sm" className="me-1" /> Resolving Media...
                        </>
                      ) : (
                        <>
                          <i className="fa fa-camera me-1"></i> View Attachments ({attachedMediaIds.length})
                        </>
                      )}
                    </Button>
                  </div>
                )
              )}

              {/* Status and Reject Remarks for View mode */}
              {status !== "Apply" && (
                <div className="border-top pt-3 mt-3">
                  <Row>
                    <Col md={6}>
                      <Label className="fw-bold d-block">Status</Label>
                      <Badge
                        color={
                          leaveData.LeaveStatus === 1 || String(currentStatusLabel).toLowerCase() === "raised"
                            ? "warning"
                            : leaveData.LeaveStatus === 2 || String(currentStatusLabel).toLowerCase() === "approved"
                              ? "success"
                              : "danger"
                        }
                        className="p-2 fs-6"
                      >
                        {currentStatusLabel}
                      </Badge>
                      {leaveData.UpdatedAt && (
                        <div className="text-muted small mt-1">
                          Updated: {moment(leaveData.UpdatedAt).format("DD-MM-YYYY, hh:mm A")}
                        </div>
                      )}
                    </Col>
                    {leaveData.LeaveRejectRemark && (
                      <Col md={6}>
                        <Label className="fw-bold d-block text-danger">Rejection / Cancel Remark</Label>
                        <div className="p-2 border bg-light rounded text-dark">
                          {leaveData.LeaveRejectRemark}
                        </div>
                      </Col>
                    )}
                  </Row>
                </div>
              )}
            </div>
          )}
        </ModalBody>

        <ModalFooter className="border-top">
          {status === "Apply" ? (
            <>
              <Button color="primary" onClick={handlePreSubmit} disabled={submitting || loader}>
                {submitting ? "Submitting..." : "Submit Leave"}
              </Button>
              <Button color="secondary" outline onClick={toggle} disabled={submitting}>
                Cancel
              </Button>
            </>
          ) : (
            <>
              {teamView && String(currentStatusLabel).toLowerCase() === "raised" && (
                <>
                  <Button
                    color="success"
                    onClick={() => handleApproveReject("approve")}
                    disabled={submitting}
                  >
                    <i className="fa fa-check me-1"></i> Approve Request
                  </Button>
                  <Button
                    color="danger"
                    onClick={() => handleApproveReject("reject")}
                    disabled={submitting}
                  >
                    <i className="fa fa-times me-1"></i> Reject Request
                  </Button>
                </>
              )}

              {teamView && String(currentStatusLabel).toLowerCase() === "approved" && (
                <Button
                  color="danger"
                  onClick={() => handleApproveReject("cancel")}
                  disabled={submitting}
                >
                  <i className="fa fa-ban me-1"></i> Cancel Leave
                </Button>
              )}

              <Button color="secondary" outline onClick={toggle}>
                Close
              </Button>
            </>
          )}
        </ModalFooter>
      </Modal>

      {/* Attachments List Modal (Images vs PDFs Tabs) */}
      <Modal isOpen={mediaModalOpen} toggle={() => setMediaModalOpen(false)} size="lg" centered>
        <ModalHeader toggle={() => setMediaModalOpen(false)}>
          <i className="fa fa-folder-open-o me-2"></i> Attachments ({resolvedMedia.images.length + resolvedMedia.pdfs.length})
        </ModalHeader>
        <ModalBody>
          <Nav tabs className="mb-3 border-bottom">
            <NavItem>
              <NavLink
                className={`fw-bold style-cursor ${selectedMediaTab === "images" ? "active text-primary border-primary" : "text-secondary"}`}
                style={{ cursor: "pointer" }}
                onClick={() => setSelectedMediaTab("images")}
              >
                <i className="fa fa-file-image-o me-1"></i> Images ({resolvedMedia.images.length})
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={`fw-bold style-cursor ${selectedMediaTab === "pdfs" ? "active text-primary border-primary" : "text-secondary"}`}
                style={{ cursor: "pointer" }}
                onClick={() => setSelectedMediaTab("pdfs")}
              >
                <i className="fa fa-file-pdf-o me-1 text-danger"></i> PDFs ({resolvedMedia.pdfs.length})
              </NavLink>
            </NavItem>
          </Nav>

          {mediaLoading ? (
            <Loader height="200px" />
          ) : selectedMediaTab === "images" ? (
            resolvedMedia.images.length === 0 ? (
              <div className="text-center py-4 text-muted">No image attachments available</div>
            ) : (
              <Row className="g-3">
                {resolvedMedia.images.map((img, idx) => (
                  <Col key={img.id} xs={6} sm={4} md={3}>
                    <div className="border rounded p-2 text-center bg-light shadow-sm">
                      <img
                        src={img.url}
                        alt={img.fileName}
                        style={{ width: "100%", height: "110px", objectFit: "cover", cursor: "pointer" }}
                        className="rounded mb-2 border"
                        onClick={() =>
                          openImageViewer(
                            resolvedMedia.images.map((i) => ({ url: i.url, fileName: i.fileName })),
                            idx
                          )
                        }
                      />
                      <div className="small fw-semibold text-truncate mb-2">{img.fileName}</div>
                      <Button
                        color="primary"
                        size="sm"
                        className="w-100"
                        onClick={() =>
                          openImageViewer(
                            resolvedMedia.images.map((i) => ({ url: i.url, fileName: i.fileName })),
                            idx
                          )
                        }
                      >
                        <i className="fa fa-eye me-1"></i> View
                      </Button>
                    </div>
                  </Col>
                ))}
              </Row>
            )
          ) : resolvedMedia.pdfs.length === 0 ? (
            <div className="text-center py-4 text-muted">No PDF attachments available</div>
          ) : (
            <div className="d-flex flex-column gap-2">
              {resolvedMedia.pdfs.map((pdf) => (
                <div key={pdf.id} className="d-flex align-items-center justify-content-between p-3 border rounded bg-light">
                  <div className="d-flex align-items-center gap-3">
                    <div className="bg-danger text-white rounded p-2 text-center" style={{ width: 44, height: 44 }}>
                      <i className="fa fa-file-pdf-o fs-5"></i>
                    </div>
                    <div>
                      <div className="fw-bold">{pdf.fileName}</div>
                      <div className="text-muted small">Media ID: #{pdf.id}</div>
                    </div>
                  </div>
                  <Button
                    color="primary"
                    size="sm"
                    onClick={() => window.open(pdf.url, "_blank", "noopener,noreferrer")}
                  >
                    <i className="fa fa-external-link me-1"></i> Open
                  </Button>
                </div>
              ))}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setMediaModalOpen(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Image Lightbox Viewer Modal */}
      <Modal isOpen={imageViewerOpen} toggle={() => setImageViewerOpen(false)} size="lg" centered>
        <ModalHeader toggle={() => setImageViewerOpen(false)}>
          {imageViewerList[imageViewerIndex]?.fileName || "Image Viewer"}{" "}
          <span className="text-muted fs-6">
            ({imageViewerIndex + 1}/{imageViewerList.length})
          </span>
        </ModalHeader>
        <ModalBody className="text-center bg-dark p-3">
          {imageViewerList[imageViewerIndex]?.url && (
            <img
              src={imageViewerList[imageViewerIndex].url}
              alt="Full View"
              style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }}
              className="rounded"
            />
          )}
        </ModalBody>
        <ModalFooter className="justify-content-between">
          <div>
            <Button
              color="secondary"
              size="sm"
              disabled={imageViewerIndex === 0}
              onClick={() => setImageViewerIndex((prev) => Math.max(prev - 1, 0))}
              className="me-2"
            >
              <i className="fa fa-chevron-left me-1"></i> Previous
            </Button>
            <Button
              color="secondary"
              size="sm"
              disabled={imageViewerIndex === imageViewerList.length - 1}
              onClick={() => setImageViewerIndex((prev) => Math.min(prev + 1, imageViewerList.length - 1))}
            >
              Next <i className="fa fa-chevron-right ms-1"></i>
            </Button>
          </div>
          <Button color="secondary" outline onClick={() => setImageViewerOpen(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Confirmation Modal */}
      <Modal isOpen={showConfirmationModal} toggle={() => setShowConfirmationModal(false)} centered size="sm">
        <ModalHeader toggle={() => setShowConfirmationModal(false)}>Confirm Leave Application</ModalHeader>
        <ModalBody>
          <p>
            Are you sure you want to apply for <strong>{selectedLeaveType}</strong> from{" "}
            <strong>{fromDate}</strong> to <strong>{toDate}</strong> ({leaveDaysCount} day{leaveDaysCount > 1 ? "s" : ""})?
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={createLeave} disabled={submitting}>
            {submitting ? "Submitting..." : "Yes, Apply"}
          </Button>
          <Button color="secondary" outline onClick={() => setShowConfirmationModal(false)} disabled={submitting}>
            No
          </Button>
        </ModalFooter>
      </Modal>

      {/* Rejection / Cancellation Remark Modal */}
      <Modal isOpen={openRemarkModal} toggle={() => setOpenRemarkModal(false)} centered>
        <ModalHeader toggle={() => setOpenRemarkModal(false)}>
          {leaveAction === "cancel" ? "Cancel Leave Request" : "Reject Leave Request"}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label className="fw-bold">Reason / Remark <span className="text-danger">*</span></Label>
            <Input
              type="textarea"
              rows={3}
              value={rejectionRemark}
              onChange={(e) => setRejectionRemark(e.target.value)}
              placeholder="Enter reason for rejection/cancellation..."
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button
            color="danger"
            onClick={submitRejectOrCancel}
            disabled={submitting || !rejectionRemark.trim()}
          >
            {submitting ? "Processing..." : leaveAction === "cancel" ? "Confirm Cancel" : "Confirm Reject"}
          </Button>
          <Button color="secondary" outline onClick={() => setOpenRemarkModal(false)} disabled={submitting}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default ApplyLeaveModal;
